import { useEffect } from 'react';
import { UseSuspenseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import {
    QuickBooksDetailsResponse,
    QuickBooksProcessMode,
    fetchQuickBooksDetails,
} from 'api/quickBooks';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';
import { create } from 'zustand';

type UseQuickBooksDetailsOptions = Omit<
    UseSuspenseQueryOptions<QuickBooksDetailsResponse, GivebutterAxiosError>,
    'queryKey' | 'queryFn' | 'select'
>;

interface QuickBooksStore {
    forcedSync: boolean;
    setForcedSync: (value: boolean) => void;
}

const useQuickBooksStore = create<QuickBooksStore>(set => ({
    forcedSync: false,
    setForcedSync: value => set({ forcedSync: value }),
}));

const useQuickBooksDetails = (options?: UseQuickBooksDetailsOptions) => {
    const { accountId } = useCurrentAccount();
    const { forcedSync, setForcedSync } = useQuickBooksStore();

    const query = useSuspenseQuery({
        queryKey: accountQueryKeys.quickBooksDetails(accountId),
        queryFn: () => fetchQuickBooksDetails(accountId),
        select: data => {
            return {
                ...data,
                process_mode_raw: data.process_mode,
            };
        },
        ...options,
    });

    useEffect(() => {
        if (forcedSync && query.data.process_mode_raw !== QuickBooksProcessMode.Idle) {
            setForcedSync(false);
        }
    }, [forcedSync, query.data.process_mode_raw, setForcedSync]);

    return query;
};

export { useQuickBooksDetails, useQuickBooksStore };
export type { UseQuickBooksDetailsOptions };
